import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * A custom element that displays recently viewed products.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} container - The container element for the products.
 * @extends {Component<Refs>}
 */
class NjRecentlyViewedComponent extends Component {
  requiredRefs = ['container'];

  connectedCallback() {
    super.connectedCallback();
    this.#loadRecentlyViewedProducts();
  }

  /**
   * Loads recently viewed products from localStorage and fetches their markup.
   */
  async #loadRecentlyViewedProducts() {
    const viewedProducts = RecentlyViewed.getProducts();
    
    if (viewedProducts.length === 0) {
      // Hide the section if there are no recently viewed products
      this.style.display = 'none';
      return;
    }

    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      if (!markup) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(markup, 'text/html');
      const productsContainer = doc.querySelector('[ref="container"]');
      
      if (!productsContainer) return;

      // Reorder products to match the order in localStorage
      const productElements = Array.from(productsContainer.querySelectorAll('[data-product-id]'));
      const reorderedProducts = [];

      for (const productId of viewedProducts) {
        const productElement = productElements.find(
          (el) => el.dataset.productId === productId
        );
        if (productElement) {
          reorderedProducts.push(productElement);
        }
      }

      // Clear and repopulate container
      if (reorderedProducts.length > 0) {
        productsContainer.innerHTML = '';
        reorderedProducts.forEach((el) => productsContainer.appendChild(el));
        morph(this.refs.container, productsContainer);
      } else {
        // No products found, hide section
        this.style.display = 'none';
      }
    } catch (error) {
      console.error('Error loading recently viewed products:', error);
      this.style.display = 'none';
    }
  }
}

if (!customElements.get('nj-recently-viewed')) {
  customElements.define('nj-recently-viewed', NjRecentlyViewedComponent);
}
